export type UserRole = 'ADMIN' | 'DISPATCHER' | 'TECHNICIAN';

export type OrderStatus =
  | 'NEW'
  | 'ASSIGNED'
  | 'EN_ROUTE'
  | 'ARRIVED'
  | 'IN_PROGRESS'
  | 'CLIENT_DECLINED'
  | 'PAUSED'
  | 'COMPLETED'
  | 'CANCELLED';

export interface User {
  id: string;
  role: UserRole;
  fullName: string;
  phone: string;
  email?: string | null;
  commissionRate?: string;
  isActive?: boolean;
  isGreaseTrapTeam?: boolean;
  forceNightOrdersEnabled?: boolean;
  isElectrician?: boolean;
  /** Вход до «Составов» — работает по-старому (без бригад) */
  oldScheme?: boolean;
  /** Удалён полностью: в «Сотрудниках» не показывается, имя остаётся в истории */
  deletedAt?: string | null;
}

export interface OrderStatusEvent {
  id: string;
  status: OrderStatus;
  changedAt: string;
  changedById: string | null;
}

export interface OrderAct {
  id: string;
  actNumber: number;
  materialsCost: string | null;
  cashAmount: string | null;
  nonCashAmount: string | null;
  qrAmount: string | null;
  sentToClientAt: string | null;
  sentTo1cAt: string | null;
  bitrixSyncedAt: string | null;
  publicToken: string;
  pdfPath: string | null;
  receiptPath: string | null;
  updPath: string | null;
  oldStyleActPath: string | null;
  schetDogovorPath: string | null;
  invoicePath?: string | null;
  report?: {
    totalAmount: string;
    notes: string | null;
    /** «Монтажные работы» — доля мастера 30% */
    installationWork?: boolean;
    startedAt: string;
    completedAt: string | null;
    workItems?: WorkItem[];
    photos?: { id: string; filePath: string; phase: 'BEFORE' | 'AFTER'; createdAt: string }[];
  } | null;
}

export interface Order {
  id: string;
  orderNumber?: number;
  bitrixDealId: string | null;
  address: string;
  clientName: string;
  clientPhone: string;
  description: string | null;
  status: OrderStatus;
  scheduledAt: string | null;
  technicianId: string | null;
  technician?: { id: string; fullName: string; phone: string } | null;
  /** Наряд бригады (вкладка «Составы») — его видят и ведут все её мастера */
  crewId?: string | null;
  crew?: { id: string; name: string } | null;
  statusEvents?: OrderStatusEvent[];
  createdAt: string;
  updatedAt: string;
  act?: OrderAct | null;
  requestType?: string | null;
  appealType?: string | null;
  paymentType?: string | null;
  additionalContacts?: { name: string; phone: string }[] | null;
  sellerOrganization?: string | null;
  isLegalEntity?: boolean;
  companyName?: string | null;
  companyInn?: string | null;
  companyKpp?: string | null;
  companyOgrn?: string | null;
  companyAddress?: string | null;
  createdByTechnician?: boolean;
}

export interface WorkItem {
  id: string;
  name: string;
  unit: string;
  quantity: string;
  pricePerUnit: string;
  amount: string;
  isSelfProposed: boolean;
}

export interface Report {
  id: string;
  notes: string | null;
  materialsCost: string | null;
  totalAmount: string;
  /** «Монтажные работы» — доля мастера 30% */
  installationWork?: boolean;
  startedAt: string;
  completedAt: string | null;
  workItems?: WorkItem[];
  technician?: { id: string; fullName: string };
}

export interface Act {
  id: string;
  actNumber: number;
  orderId: string;
  reportId: string;
  pdfPath: string | null;
  receiptPath: string | null;
  updPath: string | null;
  oldStyleActPath: string | null;
  schetDogovorPath: string | null;
  invoicePath?: string | null;
  materialsCost: string | null;
  cashAmount: string | null;
  nonCashAmount: string | null;
  qrAmount: string | null;
  clientPhone: string;
  publicToken: string;
  sentToClientAt: string | null;
  sentTo1cAt: string | null;
  bitrixSyncedAt: string | null;
  createdAt: string;
  order?: Order;
  report?: Report;
}

export type ChatAttachmentKind = 'PHOTO' | 'VIDEO' | 'VOICE' | 'FILE' | 'VIDEO_NOTE';

export interface ChatAttachment {
  id: string;
  kind: ChatAttachmentKind;
  fileName: string;
  mimeType: string;
  size: number;
  durationSec: number | null;
  /** Подписанная ссылка от корня сервера, без входа (см. ChatService.fileUrl) */
  url: string;
}

/** Цитата в «ответе на сообщение» */
export interface ChatQuote {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  deleted: boolean;
  photoUrl: string | null;
}

/** Общее у личного сообщения и сообщения канала */
interface ChatMessageBase {
  id: string;
  senderId: string;
  sender: { id: string; fullName: string };
  /** Текст для старых приложений: подпись или «📷 Фото», «Сообщение удалено» */
  text: string;
  /** Настоящая подпись, может быть пустой */
  body: string;
  createdAt: string;
  editedAt: string | null;
  deleted: boolean;
  replyTo: ChatQuote | null;
  attachments: ChatAttachment[];
}

export interface ChatMessage extends ChatMessageBase {
  receiverId: string;
  readAt: string | null;
}

export interface ChatThread {
  /** online — открыт чат прямо сейчас; lastSeenAt — когда был(а) в сети последний раз (null — не заходил(а) с 15.10.2026) */
  technician: { id: string; fullName: string; online: boolean; lastSeenAt: string | null };
  lastMessage: ChatMessage | null;
  unreadCount: number;
}

export type ChatChannel = 'general' | 'grease-trap';

export interface ReportMediaFile {
  id: string;
  filePath: string;
  createdAt: string;
}

export interface OrderMediaGroup {
  reportId: string;
  orderId: string;
  address: string;
  clientName: string;
  technicianName: string;
  reportCreatedAt: string;
  mediaUpdatedAt: string;
  media: ReportMediaFile[];
}

export interface GeneralChatMessage extends ChatMessageBase {
  channel: ChatChannel;
}

export interface GpsPosition {
  technicianId: string;
  technicianName: string;
  deviceId: string | null;
  lat: number;
  lng: number;
  recordedAt: string;
  // false — телефон не выходил на связь дольше 5 минут; точка на карте остаётся
  // (последнее известное место за смену), но помечается как «не на связи»
  isOnline: boolean;
}

export interface GpsTrail {
  technicianId: string;
  technicianName: string;
  deviceId: string | null;
  points: { lat: number; lng: number; recordedAt: string }[];
}

export interface Earnings {
  completedOrders: number;
  totalRevenue: number;
  commissionRate: number;
  earnings: number;
  sinceLastPayoutAt: string | null;
}

// Отметка «мне выдали зарплату», которую сантехник ставит сам в приложении
export interface Payout {
  id: string;
  technicianId: string;
  amount: string;
  paidAt: string;
}

export interface AppNotification {
  id: string;
  userId: string;
  type: string;
  title: string;
  body: string;
  orderId: string | null;
  /** Из какого чата: «direct:<id сантехника>», «channel:general» */
  chatKey?: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface TechnicianReport {
  id: string;
  technicianId: string;
  technician: { id: string; fullName: string };
  periodType: 'DAILY' | 'WEEKLY' | 'OLD_STYLE';
  periodStart: string;
  periodEnd: string;
  pdfPath: string;
  totalAmount: string | null;
  totalEarnings: string | null;
  createdAt: string;
}

// Ведомость за период (GET /technician-reports/summary) — считается на лету по сданным
// актам, ничего предварительно формировать не нужно. Суммы приходят числами в рублях,
// уже сведёнными: сумма дней равна итогу по сантехнику, сумма сантехников — общему итогу
export interface SummaryDay {
  date: string;
  visits: number;
  revenue: number;
  earnings: number;
}

export interface SummaryTechnician {
  id: string;
  fullName: string;
  isActive: boolean;
  /** Удалён полностью — его отчёты остаются в истории */
  deleted?: boolean;
  commissionRate: number;
  visits: number;
  revenue: number;
  materials: number;
  earnings: number;
  days: SummaryDay[];
}

// Один акт из периода (GET /technician-reports/acts) — строка, из которой собирается
// и раскрытый день, и раздел по договорам. Суммы те же, что в ведомости
export interface PeriodAct {
  /** Строка — участник наряда: у бригадного наряда строк столько, сколько людей */
  id: string;
  reportId?: string;
  /** Первая строка наряда — только по ней складывается оборот (бригадный наряд — один раз) */
  primary?: boolean;
  /** Сколько человек в наряде */
  crewSize?: number;
  actNumber: string;
  orderNumber: number;
  date: string;
  completedAt: string;
  technicianId: string;
  technicianName: string;
  address: string;
  clientName: string;
  isLegalEntity: boolean;
  companyName: string | null;
  companyInn: string | null;
  payment: 'CASH' | 'NON_CASH' | 'MIXED' | 'UNKNOWN';
  revenue: number;
  materials: number;
  earnings: number;
  /** «Монтажные работы» — доля мастера 30% */
  installationWork?: boolean;
}

export interface PeriodSummary {
  from: string;
  to: string;
  technicians: SummaryTechnician[];
  totals: { visits: number; revenue: number; materials: number; earnings: number };
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  NEW: 'Новый',
  ASSIGNED: 'Назначен',
  EN_ROUTE: 'В пути',
  ARRIVED: 'На месте',
  IN_PROGRESS: 'В работе',
  CLIENT_DECLINED: 'Отказ клиента',
  PAUSED: 'Остановлен',
  COMPLETED: 'Завершён',
  CANCELLED: 'Отменён',
};

export const ORDER_STATUSES_ORDERED: OrderStatus[] = [
  'NEW',
  'ASSIGNED',
  'EN_ROUTE',
  'ARRIVED',
  'IN_PROGRESS',
  'PAUSED',
  'CLIENT_DECLINED',
  'COMPLETED',
  'CANCELLED',
];

// Проверка контрагента (клиента-юрлица или ИП) по данным ФНС — см. backend/src/counterparties
export interface CounterpartyProblem {
  code: string;
  text: string;
}

export interface CounterpartyCheck {
  id: string;
  inn: string;
  ourName: string | null;
  ourKpp: string | null;
  found: boolean;
  type: 'LEGAL' | 'INDIVIDUAL' | null;
  name: string | null;
  status: 'ACTIVE' | 'LIQUIDATING' | 'LIQUIDATED' | 'BANKRUPT' | 'REORGANIZING' | null;
  kpp: string | null;
  ogrn: string | null;
  address: string | null;
  manager: string | null;
  liquidationDate: string | null;
  problems: CounterpartyProblem[];
  checkedAt: string;
  problemsChangedAt: string | null;
  lastError: string | null;
}

export interface CounterpartyRunInfo {
  startedAt: string;
  finishedAt: string | null;
  total: number;
  checked: number;
  withProblems: number;
  failed: number;
}

export interface CounterpartyList {
  configured: boolean;
  running: boolean;
  lastRun: CounterpartyRunInfo | null;
  items: CounterpartyCheck[];
}

export const COUNTERPARTY_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Действует',
  LIQUIDATING: 'Ликвидируется',
  LIQUIDATED: 'Ликвидирована',
  BANKRUPT: 'Банкротство',
  REORGANIZING: 'Реорганизация',
};

// Справочник: объекты обслуживания, услуги, правила (см. backend/src/kb)
export type KbSection = 'OBJECT' | 'SERVICE' | 'RULE' | 'OTHER';

export const KB_SECTION_LABELS: Record<KbSection, string> = {
  OBJECT: 'Объекты',
  SERVICE: 'Услуги',
  RULE: 'Правила',
  OTHER: 'Прочее',
};

export interface KbFile {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  createdAt: string;
  /** Путь на сервере API с подписью: /kb/files/:id?e=…&s=… */
  url: string;
}

export interface KbArticle {
  id: string;
  section: KbSection;
  title: string;
  body: string;
  tags: string[];
  matchInns: string[];
  matchAddresses: string[];
  matchPhones: string[];
  staffOnly: boolean;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  files: KbFile[];
  /** Только в карточках к наряду: почему подошла */
  reason?: string;
}

export interface KbRevision {
  id: string;
  action: string;
  editedByName: string | null;
  createdAt: string;
  snapshot: Pick<KbArticle, 'section' | 'title' | 'body' | 'tags' | 'staffOnly' | 'archived'>;
}

export interface KbSearchResult {
  articles: { id: string; section: KbSection; title: string; snippet: string }[];
  prices: { id: string; name: string; defaultUnit: string; defaultPrice: string | null }[];
}

export interface PriceItem {
  id: string;
  name: string;
  defaultUnit: string;
  defaultPrice: string | null;
  isActive: boolean;
  forElectricians: boolean;
}

// --- Обслуживание: ежемесячные сделки в Битриксе (backend/src/maintenance) ---

export type MaintenanceRunStatus = 'PENDING' | 'CREATED' | 'FAILED' | 'SKIPPED' | 'MANUAL';

export interface MaintenanceDeal {
  id: string;
  kind: string;
  templateDealId: string;
  titleTemplate: string;
  nextTitle: string;
  amount: number;
  amountEditable: boolean;
  productRows: { name: string; price: number; quantity: number }[];
  stageId: string;
  stageName: string;
  brigadeUserId: string | null;
  brigadeName: string | null;
  assignedById: string | null;
  assignedName: string | null;
  masterComment: string | null;
  timeComment: string | null;
  address: string | null;
  periodUnit: 'MONTH' | 'WEEK' | 'MONTH_DAYS' | 'MONTH_WEEKDAYS';
  every: number;
  weekday: number | null;
  /** Числа месяца (MONTH_DAYS, 31 — последний день) */
  days: number[];
  /** Какие по счёту дни недели месяца (MONTH_WEEKDAYS, 5 — последний) */
  weeks: number[];
  leadDays: number;
  createDay: number;
  workDay: number | null;
  workTime: string | null;
  forPreviousMonth: boolean;
  startPeriod: string;
  active: boolean;
  category: 'MAINTENANCE' | 'INSPECTION';
  inspectionType: string | null;
  entrances: number | null;
  schedule: string;
  /** «1м/чт», «2м» — как в столбце «Количество выездов» плана-графика */
  frequency: string;
  current: { key: string; status: MaintenanceRunStatus; dealId: string | null; error: string | null } | null;
  currentKey: string | null;
  next: { key: string; date: string; workDate: string | null } | null;
  contactsCount: number;
  hasRequisites: boolean;
}

export interface MaintenanceObject {
  id: string;
  name: string;
  address: string;
  note: string;
  bitrixCompanyId: string | null;
  companyTitle: string | null;
  deals: MaintenanceDeal[];
}

export interface MaintenanceList {
  configured: boolean;
  period: string;
  today: string;
  objects: MaintenanceObject[];
}

export interface MaintenanceTemplate {
  dealId: string;
  title: string;
  kind: string;
  titleTemplate: string;
  companyId: string | null;
  companyTitle: string | null;
  address: string | null;
  amount: number;
  productRows: { PRODUCT_NAME?: string; PRICE?: number | string; QUANTITY?: number | string }[];
  contacts: unknown[];
  requisiteLink: unknown | null;
  stageId: string;
  masterComment: string | null;
  timeComment: string | null;
  brigadeUserId: string | null;
  assignedById: string | null;
  createdDay: number | null;
  forPreviousMonth: boolean;
  workDay: number | null;
  workTime: string | null;
}

export interface MaintenanceStage {
  id: string;
  name: string;
  categoryId: string;
  categoryName: string;
}

export interface MaintenanceUser {
  id: string;
  name: string;
}

export interface MaintenanceBrigade {
  id: string;
  name: string;
  active: boolean;
}

export interface MaintenanceHistoryItem {
  period: string;
  status: MaintenanceRunStatus;
  dealId: string | null;
  error: string | null;
  at: string;
  title: string | null;
  amount: number | null;
  stageName: string | null;
  deletedInBitrix: boolean;
}

export interface MaintenanceSuggestion {
  companyId: string;
  companyTitle: string;
  kind: string;
  months: number;
  perMonth: number;
  lastDealId: string;
  lastTitle: string;
  amount: number | null;
  typicalDay: number;
  address: string | null;
  currentMonthDealId: string | null;
  alreadyConfigured: boolean;
}

export interface MaintenancePreview {
  period: string;
  workDate: string | null;
  fields: { code: string; label: string; value: unknown }[];
  contacts: number;
  rows: { name: string; price: number; quantity: number }[];
  requisites: boolean;
}

/** Расписание, как его поймёт сервер, — до сохранения (POST /maintenance/schedule/preview) */
export interface SchedulePreview {
  problem: string | null;
  schedule: string | null;
  frequency: string | null;
  upcoming: { key: string; createDate: string; workDate: string | null; title: string | null }[];
}

// --- ПрофОсмотры: план-график (тот же backend/src/maintenance, category = INSPECTION) ---

export type InspectionVisitStatus = 'PLANNED' | 'CREATED' | 'FAILED' | 'SKIPPED' | 'PENDING';

export interface InspectionVisit {
  date: string;
  createDate: string | null;
  status: InspectionVisitStatus;
  dealId: string | null;
  error: string | null;
}

export type ServiceObjectInfo = Omit<MaintenanceObject, 'deals'>;

export interface InspectionLine {
  id: string;
  object: ServiceObjectInfo;
  address: string;
  type: string | null;
  entrances: number | null;
  schedule: string;
  frequency: string;
  brigadeName: string | null;
  stageName: string;
  active: boolean;
  visits: InspectionVisit[];
  deal: MaintenanceDeal;
}

export interface InspectionPlan {
  configured: boolean;
  month: string;
  today: string;
  days: number;
  lines: InspectionLine[];
}

/** Профосмотр дома в Битриксе — кандидат в сделки-образцы */
export interface InspectionTemplateOption {
  dealId: string;
  title: string;
  stageName: string;
  date: string | null;
  companyId: string | null;
  companyTitle: string | null;
  address: string | null;
}

export interface InspectionSchedule {
  periodUnit: 'MONTH_DAYS' | 'MONTH_WEEKDAYS' | 'WEEK';
  every: number;
  weekday: number | null;
  days: number[];
  weeks: number[];
}

export interface InspectionImportRow {
  row: number;
  address: string;
  type: string;
  entrances: number | null;
  days: number[];
  frequencyText: string | null;
  schedule: InspectionSchedule | null;
  scheduleText: string | null;
  frequency: string | null;
  templateDealId: string | null;
  candidates: number;
  objectName: string | null;
  exists: boolean;
  warnings: string[];
  template: InspectionTemplateOption | null;
}

export interface InspectionImportPreview {
  sheets: { name: string; period: string | null; rows: number; problem: string | null }[];
  sheet: string;
  period: string;
  monthLabel: string;
  startPeriod: string;
  bitrixError: string | null;
  rows: InspectionImportRow[];
}

export interface InspectionImportResult {
  created: number;
  skipped: number;
  failed: number;
  results: { address: string; inspectionType: string; status: 'created' | 'skipped' | 'failed'; reason?: string; id?: string }[];
}

// Вкладка «Составы» (GET /crews): бригады от 1 до 5 мастеров со своим процентом у каждого
export interface CrewPerson {
  userId: string;
  fullName: string;
  phone: string;
  /** Номер сотрудника в Битриксе; пусто — ещё не зарегистрирован */
  bitrixUserId: string | null;
  email: string | null;
  /** Общий вход бригады из прошлых времён — в Битриксе не регистрируется */
  shared: boolean;
  /** Какая служебная почта получится при регистрации (однофамильцу — с цифрой) */
  emailPreview: string | null;
}

export interface CrewMemberInfo extends CrewPerson {
  /** Процент с обычных работ, в процентах: 10 — это 10% */
  percent: number;
  active: boolean;
}

export interface CrewInfo {
  id: string;
  name: string;
  /** Уже есть в списке поля «Бригада (новая)» в Битриксе */
  inBitrix: boolean;
  members: CrewMemberInfo[];
}

export interface CrewBoard {
  maxSize: number;
  bitrixConfigured: boolean;
  crews: CrewInfo[];
  free: (CrewPerson & { personalPercent: number })[];
  /** Входы до «Составов» — работают по-старому: без бригад и регистрации в Битриксе */
  oldScheme: { userId: string; fullName: string; phone: string; shared: boolean; personalPercent: number }[];
  history: { id: string; at: string; who: string; action: string; by: string | null }[];
  /** Ответ на изменение: список в Битриксе не обновился */
  bitrixError?: string | null;
}

/** Ответ общего поиска панели (GET /search, поле «Найти…» в меню) */
export interface GlobalSearchResults {
  /** Ничего не нашлось, а в русской раскладке нашлось — что на самом деле искали */
  switchedTo: string | null;
  orders: {
    id: string;
    orderNumber: number;
    address: string;
    clientName: string;
    clientPhone: string;
    companyName: string | null;
    status: OrderStatus;
    scheduledAt: string | null;
    technicianName: string | null;
  }[];
  acts: { id: string; actNumber: number; orderId: string; orderNumber: number; address: string; clientName: string; createdAt: string }[];
  people: { id: string; fullName: string; phone: string; role: UserRole; isActive: boolean }[];
  articles: { id: string; section: string; title: string; snippet: string }[];
  prices: { id: string; name: string; defaultUnit: string; defaultPrice: string | null }[];
  counterparties: { inn: string; name: string | null; status: string | null; problems: number }[];
  objects: { id: string; name: string; address: string; companyTitle: string | null }[];
  messages: { id: string; text: string; createdAt: string; senderName: string; thread: string; threadTitle: string }[];
}
